import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Text, useToken } from '@gluestack-ui/themed';
import Svg, {
  Polygon,
  Line,
  Circle,
} from 'react-native-svg';
import type { PerformanceLevelConfig } from '@/common/api/models/performanceLevelConfig';

export interface RadarDimension {
  key: string;
  label: string;
  level: string | null;
}

interface OralExamRadarProps {
  dimensions: RadarDimension[];
  performanceLevels: PerformanceLevelConfig[];
  levelWeights: Record<string, number>;
  size?: number;
}

const RING_COUNT = 4;
const HORIZONTAL_PADDING = 100;
const VERTICAL_PADDING = 28;
const LABEL_WIDTH = 92;
const LABEL_HEIGHT = 32;

const polarToCartesian = (cx: number, cy: number, radius: number, angleRad: number) => ({
  x: cx + radius * Math.cos(angleRad),
  y: cy + radius * Math.sin(angleRad),
});

const OralExamRadar: React.FC<OralExamRadarProps> = ({
  dimensions,
  performanceLevels,
  levelWeights,
  size = 192,
}) => {
  // @ts-expect-error - semantic tokens are not in the typed token map
  const accentColor = useToken('colors', 'bgBrand') as string;
  // @ts-expect-error - semantic tokens are not in the typed token map
  const mutedColor = useToken('colors', 'borderLight') as string;
  // @ts-expect-error - semantic tokens are not in the typed token map
  const gridColor = useToken('colors', 'borderLight') as string;

  const totalWidth = size + 2 * HORIZONTAL_PADDING;
  const totalHeight = size + 2 * VERTICAL_PADDING;
  const cx = totalWidth / 2;
  const cy = totalHeight / 2;
  const radius = size / 2 - 4;
  const labelRadius = radius + 14;
  const axes = dimensions.length;

  const angleFor = (index: number): number => (
    -Math.PI / 2 + (index * 2 * Math.PI) / Math.max(axes, 1)
  );

  const ratedLevels = useMemo(
    () => performanceLevels.filter((level) => level.id !== 'n/a'),
    [performanceLevels],
  );

  const maxWeight = useMemo(
    () => ratedLevels.reduce((max, level) => Math.max(max, levelWeights[level.id] ?? 0), 0) || 1,
    [ratedLevels, levelWeights],
  );

  const ringPolygons = useMemo(() => Array.from({ length: RING_COUNT }, (_, ringIndex) => {
    const ringRadius = (radius * (ringIndex + 1)) / RING_COUNT;
    const points = dimensions.map((_, index) => {
      const point = polarToCartesian(cx, cy, ringRadius, angleFor(index));
      return `${point.x},${point.y}`;
    }).join(' ');
    return { points, key: `ring-${ringIndex}` };
  }), [dimensions, radius, cx, cy, axes]); // eslint-disable-line react-hooks/exhaustive-deps

  const axisLines = useMemo(() => dimensions.map((dimension, index) => {
    const point = polarToCartesian(cx, cy, radius, angleFor(index));
    return { key: `axis-${dimension.key}`, x: point.x, y: point.y };
  }), [dimensions, radius, cx, cy, axes]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataPoints = useMemo(() => dimensions.map((dimension, index) => {
    const weight = dimension.level && dimension.level !== 'n/a'
      ? (levelWeights[dimension.level] ?? 0)
      : 0;
    const normalized = maxWeight > 0 ? weight / maxWeight : 0;
    const point = polarToCartesian(cx, cy, radius * normalized, angleFor(index));
    return {
      key: dimension.key,
      x: point.x,
      y: point.y,
      rated: !!dimension.level && dimension.level !== 'n/a',
    };
  }), [dimensions, levelWeights, maxWeight, radius, cx, cy, axes]); // eslint-disable-line react-hooks/exhaustive-deps

  const polygonPoints = useMemo(
    () => dataPoints.map((point) => `${point.x},${point.y}`).join(' '),
    [dataPoints],
  );

  const labels = useMemo(() => dimensions.map((dimension, index) => {
    const angle = angleFor(index);
    const cos = Math.cos(angle);
    const x = cx + labelRadius * cos;
    const y = cy + labelRadius * Math.sin(angle);

    let textAlign: 'left' | 'right' | 'center' = 'center';
    let left: number;
    if (cos > 0.2) {
      textAlign = 'left';
      left = x;
    } else if (cos < -0.2) {
      textAlign = 'right';
      left = x - LABEL_WIDTH;
    } else {
      textAlign = 'center';
      left = x - LABEL_WIDTH / 2;
    }

    return {
      key: `label-${dimension.key}`,
      label: dimension.label,
      left,
      top: y - LABEL_HEIGHT / 2,
      textAlign,
    };
  }), [dimensions, labelRadius, cx, cy, axes]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View style={{ width: totalWidth, height: totalHeight, position: 'relative' }}>
      <Svg width={totalWidth} height={totalHeight} viewBox={`0 0 ${totalWidth} ${totalHeight}`}>
        {ringPolygons.map((ring) => (
          <Polygon
            key={ring.key}
            points={ring.points}
            fill="none"
            stroke={gridColor}
            strokeWidth={1}
          />
        ))}
        {axisLines.map((axis) => (
          <Line
            key={axis.key}
            x1={cx}
            y1={cy}
            x2={axis.x}
            y2={axis.y}
            stroke={gridColor}
            strokeWidth={1}
          />
        ))}
        <Polygon
          points={polygonPoints}
          fill={accentColor}
          fillOpacity={0.18}
          stroke={accentColor}
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {dataPoints.map((point) => (
          <Circle
            key={point.key}
            cx={point.x}
            cy={point.y}
            r={point.rated ? 4 : 2.5}
            fill={point.rated ? accentColor : mutedColor}
          />
        ))}
      </Svg>
      {labels.map((label) => (
        <View
          key={label.key}
          style={{
            position: 'absolute',
            left: label.left,
            top: label.top,
            width: LABEL_WIDTH,
            height: LABEL_HEIGHT,
            justifyContent: 'center',
          }}
        >
          <Text size="xs" color="$textSubtle" style={{ textAlign: label.textAlign }}>
            {label.label}
          </Text>
        </View>
      ))}
    </View>
  );
};

export default OralExamRadar;
